import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  Switch,
  Tag,
  message,
} from "antd";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import { serverUrl } from "../../../../../nestserver";

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const authHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
});

const sectionTitle = {
  fontSize: 13,
  fontWeight: 600,
  color: "#0f172a",
  marginBottom: 2,
};

const sectionHint = {
  fontSize: 12,
  color: "#64748b",
  marginBottom: 12,
};

/**
 * Small switch with its own label. Form.Item hands it `checked` / `onChange`
 * through `valuePropName="checked"`, so the label always matches the value.
 */
const RequiredToggle = ({ checked, onChange }) => (
  <label
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      cursor: "pointer",
    }}
  >
    <Switch size="small" checked={checked} onChange={onChange} />
    <span style={{ fontSize: 12, color: checked ? "#0f172a" : "#64748b" }}>
      {checked ? "Required" : "Optional"}
    </span>
  </label>
);

const AddEditTrip = ({ open, onClose, onSaved, trip }) => {
  const [form] = Form.useForm();
  const [modal, modalContext] = Modal.useModal();
  const isEdit = !!trip;

  const [classIds, setClassIds] = useState([]);
  const [roster, setRoster] = useState([]);
  // Exclusions are what the user actually chose, so a student stays unchecked
  // when the class list changes and anyone newly pulled in starts included.
  const [excludedIds, setExcludedIds] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit only: students whose parent has answered, and how many per class.
  const [respondedIds, setRespondedIds] = useState([]);
  const [respondedByClass, setRespondedByClass] = useState({});
  // Edit only: who is on the form today, applied once the roster first loads.
  const seedIncluded = useRef(null);

  const rosterIds = roster.map((s) => s._id);
  const includedIds = rosterIds.filter((id) => !excludedIds.includes(id));
  const respondedCount = (trip?.approved || 0) + (trip?.rejected || 0);

  const classes = useQuery(
    "classes",
    async () => {
      const res = await axios.get(`${serverUrl}/classes`, {
        headers: authHeader(),
      });
      return res.data;
    },
    { enabled: open }
  );

  const classNameFor = (id) =>
    (classes.data || []).find((c) => c._id === id)?.name || "that class";

  /* ----------------------------- Open / reset ---------------------------- */

  useEffect(() => {
    if (!open) return;

    form.resetFields();
    setRoster([]);
    setExcludedIds([]);
    setRespondedIds([]);
    setRespondedByClass({});
    seedIncluded.current = null;

    if (!isEdit) {
      setClassIds([]);
      return;
    }

    form.setFieldsValue({
      title: trip.title,
      description: trip.description,
      dates: [moment(trip.from), moment(trip.to)],
      consent_deadline: moment(trip.consent_deadline),
      declarations: (trip.declarations || []).map((d) => ({
        key: d.key,
        label: d.label,
        required: d.required !== false,
      })),
    });

    let cancelled = false;
    setLoadingExisting(true);
    axios
      .get(`${serverUrl}/trip/${trip._id}/consents`, { headers: authHeader() })
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data?.result?.data || [];
        const answered = rows.filter((r) => r.status !== "pending");

        seedIncluded.current = new Set(rows.map((r) => String(r.student_id)));
        setRespondedIds(answered.map((r) => String(r.student_id)));
        setRespondedByClass(
          answered.reduce((acc, r) => {
            const id = String(r.class_id);
            acc[id] = (acc[id] || 0) + 1;
            return acc;
          }, {})
        );
        setClassIds((trip.classes || []).map((c) => c._id || c));
      })
      .catch(() => {
        if (!cancelled) message.error("Could not load who this form was sent to");
      })
      .finally(() => {
        if (!cancelled) setLoadingExisting(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isEdit, trip, form]);

  /* -------------------------------- Roster ------------------------------- */

  useEffect(() => {
    if (!classIds.length) {
      setRoster([]);
      return;
    }

    let cancelled = false;
    setLoadingRoster(true);

    Promise.all(
      classIds.map((id) =>
        axios
          .get(`${serverUrl}/students/classes/${id}`, { headers: authHeader() })
          .then((res) => res.data || [])
      )
    )
      .then((lists) => {
        if (cancelled) return;
        const merged = [];
        const seen = new Set();
        lists.flat().forEach((student) => {
          if (student?._id && !seen.has(student._id)) {
            seen.add(student._id);
            merged.push(student);
          }
        });
        setRoster(merged);

        if (seedIncluded.current) {
          const included = seedIncluded.current;
          setExcludedIds(
            merged.filter((s) => !included.has(s._id)).map((s) => s._id)
          );
          seedIncluded.current = null;
        } else {
          // Forget exclusions for students no longer on the list at all.
          const stillListed = new Set(merged.map((s) => s._id));
          setExcludedIds((previous) =>
            previous.filter((id) => stillListed.has(id))
          );
        }
      })
      .catch(() => {
        if (!cancelled)
          message.error("Could not load students for the selected classes");
      })
      .finally(() => {
        if (!cancelled) setLoadingRoster(false);
      });

    return () => {
      cancelled = true;
    };
  }, [classIds]);

  const handleClassChange = (nextIds) => {
    const removed = classIds.filter((id) => !nextIds.includes(id));
    const locked = removed.filter((id) => respondedByClass[id] > 0);
    if (locked.length) {
      message.warning(
        `${classNameFor(locked[0])} can't be removed — ${
          respondedByClass[locked[0]]
        } parent(s) there have already responded.`
      );
    }
    setClassIds([...nextIds, ...locked]);
  };

  /* -------------------------------- Submit ------------------------------- */

  const buildPayload = (values) => {
    const [from, to] = values.dates || [];
    return {
      title: values.title,
      description: values.description,
      from: from.toISOString(),
      to: to.toISOString(),
      consent_deadline: values.consent_deadline.toISOString(),
      classes: classIds,
      excluded_students: excludedIds,
      declarations: (values.declarations || [])
        .filter((d) => (d?.label || "").trim())
        .map((d) => ({
          ...(d.key ? { key: d.key } : {}),
          label: d.label.trim(),
          required: d.required !== false,
        })),
    };
  };

  const finish = (text) => {
    message.success(text);
    onSaved?.();
    onClose();
  };

  const sendUpdate = async (payload, resetResponses) => {
    const res = await axios.patch(
      `${serverUrl}/trip/${trip._id}`,
      { ...payload, reset_responses: resetResponses },
      { headers: authHeader() }
    );
    finish(res?.data?.result?.message || "Consent form updated");
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      return;
    }

    const [from] = values.dates || [];

    if (!classIds.length) {
      message.error("Select at least one class");
      return;
    }
    if (!includedIds.length) {
      message.error("Select at least one student");
      return;
    }
    if (values.consent_deadline.isAfter(from, "day")) {
      message.error("The respond-by date must fall on or before the start date");
      return;
    }

    const payload = buildPayload(values);

    setSaving(true);
    try {
      if (isEdit) {
        await sendUpdate(payload, false);
      } else {
        const res = await axios.post(`${serverUrl}/trip`, payload, {
          headers: authHeader(),
        });
        finish(res?.data?.result?.message || "Consent request sent to parents");
      }
    } catch (err) {
      const errorText =
        err?.response?.data?.error?.errors?.[0] || "Something went wrong";

      if (isEdit && err?.response?.status === 409) {
        // Declarations changed after parents answered — confirm before
        // sending them back to pending.
        modal.confirm({
          title: "Ask parents to respond again?",
          content: errorText,
          okText: "Save and ask again",
          cancelText: "Keep editing",
          onOk: async () => {
            try {
              await sendUpdate(payload, true);
            } catch (retryErr) {
              message.error(
                retryErr?.response?.data?.error?.errors?.[0] ||
                  "Something went wrong"
              );
            }
          },
        });
      } else {
        message.error(errorText);
      }
    } finally {
      setSaving(false);
    }
  };

  /* -------------------------------- Render ------------------------------- */

  const respondedSet = new Set(respondedIds);
  const allChecked = roster.length > 0 && includedIds.length === roster.length;

  return (
    <Modal
      title={isEdit ? "Edit Consent Form" : "New Consent Form"}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={isEdit ? "Save changes" : "Send consent request"}
      confirmLoading={saving}
      width={760}
      destroyOnClose
      styles={{ body: { maxHeight: "72vh", overflowY: "auto", paddingRight: 4 } }}
    >
      {modalContext}

      <Spin spinning={loadingExisting}>
        <Form form={form} layout="vertical" className="pt-4">
          {isEdit && respondedCount > 0 && (
            <Alert
              type="info"
              showIcon
              className="mb-4"
              message={`${respondedCount} parent(s) have already responded.`}
              description="Students they answered for stay on this form. Changing the declarations will ask those parents to respond again."
            />
          )}

          {/* ---------------------------- Details --------------------------- */}

          <Form.Item
            name="title"
            label="Consent name"
            rules={[{ required: true, message: "Enter a consent name" }]}
          >
            <Input placeholder="e.g. Field Trip" />
          </Form.Item>

          <Form.Item name="description" label="Details for parents">
            <TextArea
              autoSize={{ minRows: 3, maxRows: 10 }}
              placeholder="What it is, when, what to bring, cost, supervising staff…"
            />
          </Form.Item>

          <div style={{ display: "flex", gap: 16 }}>
            <Form.Item
              name="dates"
              label="Dates"
              style={{ flex: 1 }}
              rules={[{ required: true, message: "Pick the dates" }]}
            >
              <RangePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
            </Form.Item>

            <Form.Item
              name="consent_deadline"
              label="Parents respond by"
              style={{ flex: 1 }}
              rules={[{ required: true, message: "Pick a deadline" }]}
            >
              <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
            </Form.Item>
          </div>

          {/* ------------------------- Declarations ------------------------- */}

          <div style={{ marginTop: 8 }}>
            <div style={sectionTitle}>Declarations</div>
            <div style={sectionHint}>
              Statements each parent ticks before giving consent. Required ones
              must all be ticked; optional ones record a preference.
            </div>

            <Form.List name="declarations">
              {(fields, { add, remove }) => (
                <div style={{ display: "grid", gap: 10, marginBottom: 20 }}>
                  {fields.map(({ key, name, ...rest }, index) => (
                    <div
                      key={key}
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 10,
                        background: "#fafafa",
                        padding: "10px 12px",
                      }}
                    >
                      <Form.Item {...rest} name={[name, "key"]} hidden>
                        <Input />
                      </Form.Item>

                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          alignItems: "flex-start",
                        }}
                      >
                        <span
                          style={{
                            flexShrink: 0,
                            width: 24,
                            height: 24,
                            marginTop: 4,
                            borderRadius: "50%",
                            background: "#e2e8f0",
                            color: "#334155",
                            fontSize: 12,
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {index + 1}
                        </span>
                        <Form.Item
                          {...rest}
                          name={[name, "label"]}
                          style={{ flex: 1, minWidth: 0, marginBottom: 0 }}
                          rules={[
                            {
                              required: true,
                              whitespace: true,
                              message: "Write the statement or remove this row",
                            },
                          ]}
                        >
                          <TextArea
                            autoSize={{ minRows: 2, maxRows: 6 }}
                            style={{ width: "100%", background: "#fff" }}
                            placeholder="e.g. I give permission for my child to take part in this activity."
                          />
                        </Form.Item>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginTop: 8,
                          paddingLeft: 34,
                        }}
                      >
                        <Form.Item
                          {...rest}
                          name={[name, "required"]}
                          valuePropName="checked"
                          initialValue={true}
                          noStyle
                        >
                          <RequiredToggle />
                        </Form.Item>
                        <Button
                          type="text"
                          size="small"
                          danger
                          onClick={() => remove(name)}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))}

                  <Button
                    type="dashed"
                    block
                    onClick={() => add({ required: true })}
                  >
                    Add declaration
                  </Button>

                  {fields.length === 0 && (
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>
                      No declarations yet — parents will only sign and submit.
                    </div>
                  )}
                </div>
              )}
            </Form.List>
          </div>

          {/* ------------------------ Who receives it ----------------------- */}

          <div style={sectionTitle}>Who receives it</div>
          <div style={sectionHint}>
            Everyone in the chosen classes is included. Untick anyone who should
            not get this form.
          </div>

          <Form.Item label="Classes" required>
            <Select
              mode="multiple"
              allowClear={!isEdit}
              showSearch
              placeholder="Select one or more classes"
              optionFilterProp="label"
              value={classIds}
              onChange={handleClassChange}
              options={(classes.data || []).map((c) => ({
                label: c.name,
                value: c._id,
              }))}
            />
          </Form.Item>

          {loadingRoster ? (
            <div className="py-6 text-center">
              <Spin />
            </div>
          ) : roster.length > 0 ? (
            <div>
              <div className="flex items-center justify-between pb-2">
                <span className="font-semibold">
                  Students ({includedIds.length} of {roster.length} included)
                </span>
                <Checkbox
                  checked={allChecked}
                  indeterminate={!allChecked && includedIds.length > 0}
                  onChange={(e) =>
                    setExcludedIds(
                      e.target.checked
                        ? []
                        : rosterIds.filter((id) => !respondedSet.has(id))
                    )
                  }
                >
                  Select all
                </Checkbox>
              </div>
              <div
                className="border rounded p-3"
                style={{ maxHeight: 240, overflowY: "auto" }}
              >
                <Checkbox.Group
                  value={includedIds}
                  onChange={(checked) =>
                    setExcludedIds(
                      rosterIds.filter(
                        (id) => !checked.includes(id) && !respondedSet.has(id)
                      )
                    )
                  }
                  style={{ width: "100%" }}
                >
                  <div className="grid grid-cols-2 gap-2">
                    {roster.map((student) => {
                      const locked = respondedSet.has(student._id);
                      return (
                        <Checkbox
                          key={student._id}
                          value={student._id}
                          disabled={locked}
                        >
                          {`${student.first_name || ""} ${
                            student.last_name || ""
                          }`.trim()}
                          {locked && (
                            <Tag color="blue" style={{ marginLeft: 6 }}>
                              responded
                            </Tag>
                          )}
                        </Checkbox>
                      );
                    })}
                  </div>
                </Checkbox.Group>
              </div>
            </div>
          ) : null}
        </Form>
      </Spin>
    </Modal>
  );
};

export default AddEditTrip;
